import type { LayoutElement } from "@jod/schemas";
import { seatPositions } from "./geometry.ts";
import type { ElementTone } from "./types.ts";

/**
 * Pure SVG for each element kind, drawn CENTERED at the origin spanning
 * [-w/2..w/2] × [-h/2..h/2]. The parent applies translate + rotate. The 2.5D
 * "real" feel comes from the shadow filter, gradients and seat dots — no 3D.
 */

const TABLE_FILL: Record<ElementTone, { fill: string; stroke: string; opacity: number }> = {
  default: { fill: "var(--glass-hi)", stroke: "var(--gold-soft)", opacity: 1 },
  available: { fill: "var(--glass-hi)", stroke: "var(--lime-500)", opacity: 1 },
  occupied: { fill: "var(--amber-500)", stroke: "var(--amber-500)", opacity: 0.85 },
  reserved: { fill: "var(--gold-500)", stroke: "var(--gold-500)", opacity: 0.8 },
  disabled: { fill: "var(--charcoal-800)", stroke: "var(--hairline-strong)", opacity: 0.55 },
  selected: { fill: "var(--glass-hi)", stroke: "var(--lime-500)", opacity: 1 },
  highlight: { fill: "var(--lime-500)", stroke: "var(--lime-500)", opacity: 0.9 },
};

function Label({ text, y = 0, size = 18 }: { text: string; y?: number; size?: number }) {
  return (
    <text
      x={0}
      y={y}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={size}
      fontWeight={600}
      fill="var(--cream)"
      style={{ pointerEvents: "none", userSelect: "none" }}
    >
      {text}
    </text>
  );
}

function TableShape({
  el,
  tone,
}: {
  el: Extract<LayoutElement, { kind: "table" }>;
  tone: ElementTone;
}) {
  const v = TABLE_FILL[tone];
  const seats = seatPositions(el.shape, el.seats, el.w, el.h);
  const strokeW = tone === "selected" || tone === "highlight" ? 3 : 1.5;
  return (
    <g filter="url(#fp-shadow)">
      {seats.map((p, i) => (
        <circle
          // biome-ignore lint/suspicious/noArrayIndexKey: positional seat dots
          key={i}
          cx={p.x}
          cy={p.y}
          r={6}
          fill="var(--cream-3)"
        />
      ))}
      {el.shape === "circle" ? (
        <ellipse
          rx={el.w / 2}
          ry={el.h / 2}
          fill={v.fill}
          fillOpacity={v.opacity}
          stroke={v.stroke}
          strokeWidth={strokeW}
        />
      ) : (
        <rect
          x={-el.w / 2}
          y={-el.h / 2}
          width={el.w}
          height={el.h}
          rx={14}
          fill={v.fill}
          fillOpacity={v.opacity}
          stroke={v.stroke}
          strokeWidth={strokeW}
        />
      )}
      <Label text={el.label ?? el.code} />
    </g>
  );
}

function FixtureRect({
  el,
  fill,
  stroke,
  label,
  labelSize = 16,
}: {
  el: LayoutElement;
  fill: string;
  stroke: string;
  label: string;
  labelSize?: number;
}) {
  return (
    <g filter="url(#fp-shadow)">
      <rect
        x={-el.w / 2}
        y={-el.h / 2}
        width={el.w}
        height={el.h}
        rx={12}
        fill={fill}
        stroke={stroke}
        strokeWidth={1.5}
      />
      <Label text={label} size={labelSize} />
    </g>
  );
}

function ZoneShape({ el }: { el: Extract<LayoutElement, { kind: "zone" }> }) {
  const fill = el.fill ?? "var(--gold-500)";
  return (
    <g>
      <rect
        x={-el.w / 2}
        y={-el.h / 2}
        width={el.w}
        height={el.h}
        rx={18}
        fill={fill}
        fillOpacity={0.07}
        stroke={fill}
        strokeOpacity={0.4}
        strokeWidth={1.5}
        strokeDasharray="8 7"
      />
      {el.label ? (
        <text
          x={-el.w / 2 + 14}
          y={-el.h / 2 + 20}
          fontSize={13}
          fontWeight={700}
          letterSpacing={1.5}
          fill={fill}
          fillOpacity={0.85}
          style={{ pointerEvents: "none", userSelect: "none", textTransform: "uppercase" }}
        >
          {el.label}
        </text>
      ) : null}
    </g>
  );
}

/** Dispatch to the right shape. `tone` only affects tables. */
export function ElementShape({ el, tone }: { el: LayoutElement; tone: ElementTone }) {
  switch (el.kind) {
    case "table":
      return <TableShape el={el} tone={tone} />;
    case "stage":
      return (
        <FixtureRect
          el={el}
          fill="url(#fp-stage)"
          stroke="var(--gold-deep)"
          label={el.label ?? "STAGE"}
        />
      );
    case "bar":
      return (
        <FixtureRect
          el={el}
          fill="url(#fp-bar)"
          stroke="var(--gold-soft)"
          label={el.label ?? "BAR"}
        />
      );
    case "dancefloor":
      return (
        <g>
          <rect
            x={-el.w / 2}
            y={-el.h / 2}
            width={el.w}
            height={el.h}
            rx={10}
            fill="url(#fp-dance)"
            stroke="var(--lime-deep)"
            strokeOpacity={0.4}
            strokeWidth={1.5}
          />
          {el.label ? (
            <text
              x={0}
              y={0}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={14}
              letterSpacing={2}
              fill="var(--cream-3)"
              style={{ pointerEvents: "none", userSelect: "none", textTransform: "uppercase" }}
            >
              {el.label}
            </text>
          ) : null}
        </g>
      );
    case "entrance":
      return (
        <FixtureRect
          el={el}
          fill="var(--charcoal-800)"
          stroke="var(--lime-500)"
          label={el.label ?? "ENTRANCE"}
          labelSize={13}
        />
      );
    case "zone":
      return <ZoneShape el={el} />;
  }
}
